import chroma from "chroma-js";

/**
 * Converts a hex color code to HSL format
 * @param {string} hex - Hex color code (e.g. "#ff0000")
 * @param {Object} currentHSL - Fallback HSL values if conversion fails
 * @returns {Object} HSL values as {h, s, l} (normalized to 0-1 scale)
 */

export const hexToHSL = (hex) => {
  const validHex = hex.charAt(0) === '#' ? hex.substring(1) : hex;
  const r = parseInt(validHex.substring(0, 2), 16) / 255;
  const g = parseInt(validHex.substring(2, 4), 16) / 255;
  const b = parseInt(validHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    // Achromatic (gray)
    h = 0;
    s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0; break;
    }

    h /= 6;
  }

  // Return exact values, not rounded
  return { h, s, l };
};




/**
 * Converts HSL values to a hex color code
 * @param {number} h - Hue (0-1 or 0-360)
 * @param {number} s - Saturation (0-1)
 * @param {number} l - Lightness (0-1)
 * @param {Object} currentHsl - Fallback HSL values if conversion fails
 * @returns {string} Hex color code
 */


export const hslToHex = (h, s, l, currentHsl = { h: 0, s: 0, l: 0 }) => {
  try {
    // Use fallback values if any component is NaN
    const safeH = isNaN(h) ? currentHsl.h : h;
    const safeS = isNaN(s) ? currentHsl.s : s;
    const safeL = isNaN(l) ? currentHsl.l : l;

    // Convert hue to degrees if it's normalized (0-1)
    const hDegrees = safeH <= 1 ? safeH * 360 : safeH;
    return chroma.hsl(hDegrees, safeS, safeL).hex();
  } catch (e) {
    console.error('Invalid HSL values:', { h, s, l }, e);
    return '#000000';
  }
};











/**
 * Validates if a string is a valid hex color code
 * @param {string} hex - Hex color code to validate
 * @returns {boolean} True if valid hex code
 */
export const validateHex = (hex) => {
  const hexRegex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
  return hexRegex.test(hex);
};












/**
 * Converts a hex color code to RGB format
 * @param {string} hex - Hex color code (e.g. "#ff0000")
 * @param {Array} fallbackRgb - Fallback RGB values if conversion fails
 * @returns {Array} RGB values as [r, g, b]
 */
export const hexToRgb = (hex, fallbackRgb = [0, 0, 0]) => {
  try {
    if (!hex || typeof hex !== 'string') {
      throw new Error('Invalid hex input');
    }

    hex = hex.replace("#", "");
    if (hex.length === 3) {
      hex = hex.split("").map(c => c + c).join(""); // Convert short HEX to full HEX
    }

    if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
      throw new Error('Invalid hex format');
    }

    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return [
      isNaN(r) ? fallbackRgb[0] : r,
      isNaN(g) ? fallbackRgb[1] : g,
      isNaN(b) ? fallbackRgb[2] : b
    ];
  } catch (e) {
    console.error('Error in hexToRgb:', e);
    return fallbackRgb;
  }
};










/**
 * Parses HSL values from an HSL color string
 * @param {string} color - HSL color string or hex color
 * @returns {Object} HSL values as {h, s, l}
 */
export const getExactHslString = (color) => {
  // Handle undefined or non-string input
  if (!color || typeof color !== 'string') {
    console.warn('Invalid color input for getExactHslString:', color);
    return { h: 0, s: 0, l: 0 };
  }

  // Handle hex input directly
  if (color.startsWith('#')) {
    const { h, s, l } = hexToHSL(color);
    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  }

  // Improved regex that handles decimal values
  const hslRegex = /hsl\(\s*(\d+\.?\d*)\s*,\s*(\d+\.?\d*)\s*%\s*,\s*(\d+\.?\d*)\s*%\s*\)/;
  const match = color.match(hslRegex);

  if (match) {
    // Parse as float to handle decimal points, then round
    return {
      h: Math.round(parseFloat(match[1])),
      s: Math.round(parseFloat(match[2])),
      l: Math.round(parseFloat(match[3]))
    };
  }

  // Fallback to your existing conversion
  try {
    const { h, s, l } = hexToHSL(color);
    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  } catch (e) {
    console.error('Failed to parse color in getExactHslString:', color, e);
    return { h: 0, s: 0, l: 0 };
  }
};









/**
 * Calculates the Euclidean distance between two colors
 * @param {Array} rgb1 - First RGB color as [r, g, b]
 * @param {Array} rgb2 - Second RGB color as [r, g, b]
 * @returns {number} Distance between the colors
 */
export const colorDistance = (rgb1, rgb2) => {
  return Math.sqrt(
    Math.pow(rgb1[0] - rgb2[0], 2) +
    Math.pow(rgb1[1] - rgb2[1], 2) +
    Math.pow(rgb1[2] - rgb2[2], 2)
  );
};